import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClaySubscription,
  EntitlementName,
  ProductName,
  UserEntitlements,
  UserProfileRequest,
  UserSession,
} from '@repo/utils/types';
import { setUser as setUserInStore } from '@repo/redux/reducers/user/account';
import buildFormData from '@repo/utils/helpers/buildFormData';
import { useDispatch, useSelector } from 'react-redux';
import {
  cleanupAndLogout,
  clearNewSignUp,
  isAccessTokenValid,
  isNewMarketingSignUp,
  isRefreshTokenValid,
} from '@repo/utils/helpers/jwt';
import * as Sentry from '@sentry/react';
import analytics, { userTraits } from '@repo/utils/helpers/analytics';
import { sentryKey } from '@app/config';
import { redirectLocation } from '@repo/redux/actions/ui';
import {
  clearRewardfulAffiliateCode,
  getRewardfulAffiliateCode,
  submitNewUserUTMParams,
} from '@repo/utils/helpers/utm';
import { appVersion } from '@app/config';
import { apiRequest, checkUser } from '@repo/utils/helpers/api';
import hideLoader from '@repo/utils/helpers/hide-loader';
import { isPublicRoute } from '@app/Routes/AppRoutes';
import Contact from '@repo/utils/models/Contact';
import { AuthContext, AuthContextSchema } from '@repo/utils/context';
import { usePostHog } from '@repo/utils/helpers/posthog-js-react';
import { selectUserContactCount } from '@repo/redux/reducers/user/stats';

export function AuthProvider({ children }: PropsWithChildren<{}>) {
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const [user, setUser] = useState<UserSession | null>(null);
  const [entitlements, setEntitlements] = useState<UserEntitlements>({});
  const [subscriptions, setSubscriptions] = useState<ClaySubscription[]>([]);
  // const [onboarded, setOnboarded] = useState(user?.has_completed_onboarding);
  const [ready, setReady] = useState(false);
  const [accountProcessingStatus, setAccountProcessingStatus] = useState(null);
  const userContactCount = useSelector(selectUserContactCount);
  const contactLimit: number | null = useMemo(() => entitlements['contact.limit'], [entitlements]);

  const contactIsRestricted = useCallback(
    (contact: Contact) =>
      userContactCount && contactLimit && userContactCount > contactLimit && contact.isRestricted,
    [contactLimit, userContactCount],
  );

  useEffect(() => {
    if (!ready) {
      // get our user and user's account status
      (async () => {
        const userData = await pullUserData();

        if (!userData && !isPublicRoute(window.location.pathname)) {
          window.location.href = '/login';
          return;
        }

        // We only want to check account status if the user is logged in
        if (userData) {
          await getStatusSubscriptionsAndEntitlements();
        }

        hideLoader();
        setReady(true);
        // isNewSignUp checks for marketing sign up flow
        // has_completed_onboarding is used to grab users that created accounts outside that flow
        if (userData && (isNewMarketingSignUp() || !userData.has_completed_onboarding)) {
          await submitNewUserUTMParams(userData);

          // only redirect for new marketing sign up
          if (isNewMarketingSignUp()) {
            analytics.googleTrackConversion();
            await submitRewardfulReferral();
            clearNewSignUp();
            if (!window.app.isElectron && !userData.has_completed_onboarding) {
              posthog?.identify(String(userData?.id));
              posthog?.set_config({ autocapture: true });
              posthog?.startSessionRecording();
              dispatch(redirectLocation({ pathname: '/start' }));
            }
          }
        }
      })();
    }
  }, [ready]);

  useEffect(() => {
    window.app.on('window:focus', onFocus);

    return () => {
      window.app.off('window:focus', onFocus);
    };
  }, [user]);

  useEffect(() => {
    if (user && sentryKey) {
      Sentry.setUser({
        id: String(user.id),
        email: user.email,
      });
    }

    if (user && window.app.isElectron && window.desktopIntegrations) {
      window.desktopIntegrations.userAuthenticated();
      if (window.desktopIntegrations.setScopes) {
        window.desktopIntegrations.setScopes({
          userScopes: {
            id: `${user.id}`,
            email: user.email,
          },
        });
      }
    }

    analytics.identify(user, userTraits(user, { web_version: appVersion }));
  }, [user]);

  async function getStatusSubscriptionsAndEntitlements() {
    return Promise.all([pullAccountStatus(), pullSubscriptions(), pullEntitlements()]);
  }

  async function pullUserData(): Promise<UserSession | null> {
    if (isAccessTokenValid() || isRefreshTokenValid()) {
      try {
        const userData = await checkUser();
        if (JSON.stringify(user) !== JSON.stringify(userData)) {
          setUser(userData);
        }
        return userData;
      } catch (e) {
        // we shouldn't have to do anything - we will be redirected
        if (!!e.status || e.status !== 401) {
          console.error(e);
        }
      }
    }
    return null;
  }

  async function pullAccountStatus() {
    const accountStatus = await apiRequest<{
      enrichment: {
        total: number;
        processed: number;
      };
      status: string;
    }>('GET', '/users/self/status/');
    setAccountProcessingStatus(accountStatus?.status);
    return accountStatus?.status;
  }

  async function pollForAccountStatus() {
    const status = await pullAccountStatus();
    if (status !== 'complete') {
      setTimeout(pollForAccountStatus, 5000);
    }
  }

  async function pullSubscriptions() {
    const subscriptions = await apiRequest<{ results: ClaySubscription[] }>(
      'GET',
      '/subscriptions/',
      undefined,
      {
        apiVersion: 'v2',
      },
    );
    setSubscriptions(subscriptions?.results);
  }

  async function pullEntitlements() {
    if (isAccessTokenValid() || isRefreshTokenValid()) {
      const userEntitlementList = await apiRequest<
        {
          entitlement: EntitlementName;
          value: number | boolean | null;
        }[]
      >('GET', '/self/entitlements/', undefined, {
        apiVersion: 'v2',
      });

      let entitlements = userEntitlementList?.reduce(
        (es, e) => ({ ...es, [e.entitlement]: e.value }),
        {},
      );
      // To perform overrides locally, uncomment the following line and edit as needed. We should add manual
      // editing to the settings staging panel when there is time.
      // entitlements = { ...entitlements, 'contact.limit': 750 }
      setEntitlements(entitlements);
      return;
    }
    setEntitlements({});
  }

  function onFocus() {
    if (user) {
      analytics.track('Application Opened');
      analytics.screen('Application Opened');
    }

    pullUserData();
    pullEntitlements();
  }

  function updateLocalUserProfile(userProfile: UserSession): void {
    setUser({ ...user, ...userProfile });
  }

  async function updateUserProfile(userProfile: UserProfileRequest): Promise<boolean> {
    const updatedUser = await apiRequest<UserSession>(
      'PATCH',
      '/users/self/',
      buildFormData(userProfile),
    );

    if (updatedUser) {
      updateLocalUserProfile(updatedUser);
      // if (!onboarded && updatedUser.has_completed_onboarding) {
      //   setOnboarded(true);
      // }
      return true;
    }

    return false;
  }

  function logout(): void {
    setUser(null);
    cleanupAndLogout();
  }

  // entitlement values can be a boolean, number, or null.
  // Returning undefined if the entitlement does not exist.
  function getEntitlementValue(
    entitlementName: EntitlementName,
  ): number | boolean | null | undefined {
    if (entitlements.hasOwnProperty(entitlementName)) {
      return entitlements[entitlementName];
    }
    return undefined;
  }

  async function submitRewardfulReferral(): Promise<void> {
    const via = getRewardfulAffiliateCode();
    if (!via) {
      return;
    }

    try {
      await apiRequest(
        'POST',
        '/stripe-referral/',
        {
          referral: via,
        },
        {
          apiVersion: 'v2',
        },
      );
    } catch (e) {
      console.error(e);
    }
    clearRewardfulAffiliateCode();
  }

  function setUserPrimaryProduct(productName: ProductName): void {
    dispatch(setUserInStore({ ...user, primary_product: { name: productName } }));
  }

  function getMainSubscription(): ClaySubscription | undefined {
    return subscriptions.find(
      (sub) => sub.status === 'active' && sub.plan?.product?.name === user?.primary_product?.name,
    );
  }

  function getIsOnTrialPlan(): boolean {
    return !!getMainSubscription()?.trial;
  }

  function refreshSubscriptions(): void {
    pullSubscriptions();
  }

  function getMainSubscriptionID(): string | undefined {
    return getMainSubscription()?.provider_id;
  }

  function contactIsSelf(contact: Contact): boolean {
    if (!contact) {
      return false;
    }

    return String(user?.contact_id) === String(contact?.id);
  }

  useEffect(() => {
    dispatch(
      setUserInStore({ ...user, contactLimit: getEntitlementValue('contact.limit') as number }),
    );
  }, [user, entitlements]);

  // useEffect(() => {
  //   if (onboarded && location.pathname == '/onboarding') {
  //     dispatch(redirectLocation({ pathname: '/tour' }));
  //   }
  // }, [onboarded]);

  const value: AuthContextSchema = {
    user,
    isReady: ready,
    subscriptions,
    accountProcessingStatus,
    pollForAccountStatus,
    updateLocalUserProfile,
    updateUserProfile,
    getEntitlementValue,
    setUserPrimaryProduct,
    cleanupAndLogout: logout,
    getIsFreePlan: () => user && user.primary_product.name === 'Clay Personal',
    getIsProPlan: () => user && user.primary_product.name === 'Clay Pro',
    getIsTeamsPlan: () => user && user.primary_product.name === 'Clay Teams',
    getIsOnTrialPlan,
    getMainSubscriptionID,
    refreshSubscriptions,
    fetchUserData: pullUserData,
    contactLimit,
    contactIsRestricted,
    contactIsSelf,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
